import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, CheckCircle, Shield, AlertTriangle, DollarSign, Clock, TrendingUp, XCircle, ArrowRight, ExternalLink, Calculator } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const ShouldIAcceptSettlementPage = () => {
  const faqData = [
    {
      question: "Why do insurance companies offer quick settlements?",
      answer: "Insurance companies know that injuries often worsen over time. By settling quickly, they close your claim before you understand the full extent of your injuries and losses. It saves them money."
    },
    {
      question: "Can I negotiate a settlement offer myself?",
      answer: "You can, but research shows represented claimants receive significantly higher settlements. According to Insurance Research Council studies, people with attorneys receive settlements averaging 3.5 times higher than those without."
    },
    {
      question: "What if I've already accepted a settlement?",
      answer: "Once you sign a release, you typically cannot reopen your claim—even if your injuries turn out to be worse than expected. This is why it's critical to understand your injuries before accepting any offer."
    },
    {
      question: "How do I know if a settlement offer is fair?",
      answer: "A fair offer should cover all medical bills (past and future), lost wages, reduced earning capacity, pain and suffering, and other damages. Most initial offers fall far short of this."
    },
    {
      question: "Will hiring a lawyer actually get me more money?",
      answer: "Studies consistently show yes. The Insurance Research Council found that represented claimants receive 3.5 times higher settlements on average. Even after attorney fees, most people net more with representation."
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="should-i-accept-settlement-page">
      <SEOHead 
        title="Should I Accept the Insurance Settlement Offer? | Amy C. Brown Law"
        description="Insurance offered you a settlement after your car accident. Should you accept? Learn why early offers are often too low and how to protect yourself. Call (425) 675-1224."
        faqData={faqData}
        serviceData={{
          name: "Settlement Evaluation",
          description: "Legal evaluation of insurance settlement offers for car accident victims. Ensuring you receive fair compensation for your injuries.",
          category: "Personal Injury Law - Insurance Settlements"
        }}
      />

      {/* Hero Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <DollarSign className="w-4 h-4" />
                Settlement Guidance
              </div>
              
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-secondary mb-6 leading-tight">
                Insurance Offered a Settlement. <span className="text-primary">Should You Accept?</span>
              </h1>
              
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                You got a call from the insurance company. They're offering to settle your claim. The number sounds reasonable. You're wondering if you should just take it and move on.
              </p>

              <div className="bg-amber-50 rounded-xl p-6 mb-8 border-l-4 border-amber-500">
                <p className="text-slate-700 leading-relaxed font-medium">
                  Before you sign anything, let me share what I learned from years of working on the insurance company's side: Quick settlement offers are almost always designed to pay you less than you deserve.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a 
                  href="tel:425-675-1224"
                  className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl"
                  data-testid="hero-call-btn"
                >
                  <Phone className="w-5 h-5" />
                  Call (425) 675-1224
                </a>
                <a 
                  href="sms:425-675-1224"
                  className="inline-flex items-center justify-center gap-2 border-2 border-secondary text-secondary px-8 py-4 rounded-full font-semibold text-lg hover:bg-secondary hover:text-white transition-all"
                  data-testid="hero-text-btn"
                >
                  <MessageCircle className="w-5 h-5" />
                  Text Me
                </a>
              </div>

              <div className="flex flex-wrap gap-6 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Free settlement review
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  No obligation
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-xl border border-slate-100">
              <h3 className="font-heading text-xl font-bold text-secondary mb-6">Why Early Offers Are Low:</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="bg-amber-100 p-2 rounded-full">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-secondary">You don't know your full injuries yet</p>
                    <p className="text-slate-600 text-sm">Symptoms often worsen over days or weeks</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-amber-100 p-2 rounded-full">
                    <DollarSign className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-secondary">Future costs aren't included</p>
                    <p className="text-slate-600 text-sm">Ongoing treatment, lost wages, permanent effects</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-amber-100 p-2 rounded-full">
                    <XCircle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-secondary">Once you sign, it's final</p>
                    <p className="text-slate-600 text-sm">You can't reopen your claim later</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Studies Show */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-secondary mb-8 text-center">
            What Research Shows About Settlements
          </h2>
          
          <div className="bg-slate-50 rounded-2xl p-8 mb-8">
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              You might wonder: "Will hiring a lawyer actually help, or will I just lose money to fees?"
            </p>
            <p className="text-slate-600 leading-relaxed mb-6">
              Independent research answers this question clearly. The Insurance Research Council—the insurance industry's own research organization—has studied this extensively.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-primary/5 rounded-xl p-6 border border-primary/20">
              <TrendingUp className="w-10 h-10 text-primary mb-4" />
              <h3 className="font-heading text-2xl font-bold text-secondary mb-2">3.5x Higher</h3>
              <p className="text-slate-600">According to Insurance Research Council studies, claimants with attorneys receive settlements averaging 3.5 times higher than those without representation.</p>
              <p className="text-xs text-slate-500 mt-3">Source: Insurance Research Council, "Paying for Auto Injuries"</p>
            </div>
            <div className="bg-primary/5 rounded-xl p-6 border border-primary/20">
              <DollarSign className="w-10 h-10 text-primary mb-4" />
              <h3 className="font-heading text-2xl font-bold text-secondary mb-2">85% of Payouts</h3>
              <p className="text-slate-600">IRC data shows that 85% of all bodily injury insurance payouts go to claimants who had attorney representation.</p>
              <p className="text-xs text-slate-500 mt-3">Source: Insurance Research Council bodily injury claims analysis</p>
            </div>
          </div>

          <div className="bg-accent-soft rounded-2xl p-8 border-l-4 border-primary">
            <p className="text-slate-700 leading-relaxed">
              <strong>What this means for you:</strong> Even after paying attorney fees (typically 33% of the settlement), most people net significantly more with representation than without. A 3.5x higher settlement minus a 33% fee still leaves you with substantially more than handling it yourself.
            </p>
          </div>
        </div>
      </section>

      {/* Red Flags Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-secondary mb-8 text-center">
            Red Flags That the Offer Is Too Low
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { flag: "The offer came within days of your accident", reason: "They're trying to settle before you know your full injuries" },
              { flag: "It only covers current medical bills", reason: "What about future treatment, lost wages, pain and suffering?" },
              { flag: "They're pressuring you to decide quickly", reason: "Legitimate offers don't expire overnight" },
              { flag: "They discourage you from talking to a lawyer", reason: "They know an attorney would recognize the offer is low" },
              { flag: "The adjuster says this is their 'final' offer", reason: "First offers are almost never final—it's a negotiation tactic" },
              { flag: "You're still treating or in pain", reason: "You shouldn't settle until you've reached maximum medical improvement" }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-secondary mb-1">{item.flag}</p>
                    <p className="text-slate-600 text-sm">{item.reason}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Missing Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-secondary mb-8 text-center">
            What Early Settlement Offers Usually Leave Out
          </h2>
          
          <div className="space-y-4">
            {[
              { item: "Future medical expenses", desc: "Physical therapy, follow-up visits, surgeries, medications you'll need later" },
              { item: "Full lost wages", desc: "Not just missed days, but reduced earning capacity if you can't work like before" },
              { item: "Pain and suffering", desc: "The physical pain, emotional distress, and impact on your daily life" },
              { item: "Loss of enjoyment of life", desc: "Activities and hobbies you can no longer do because of your injuries" },
              { item: "Permanent impairment", desc: "Long-term or permanent effects of your injuries" }
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                <XCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-secondary">{item.item}</p>
                  <p className="text-slate-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Calculator CTA */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
            <Calculator className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold text-secondary mb-4">
              Get an Estimate of Your Claim's Value
            </h2>
            <p className="text-slate-600 mb-6">
              Use our free compensation estimator to get a rough idea of what your claim might be worth. Then let's talk about whether the offer you received is fair.
            </p>
            <Link 
              to="/compensation-estimator"
              className="inline-flex items-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold hover:bg-primary/90 transition-all"
            >
              <Calculator className="w-5 h-5" />
              Try the Compensation Estimator
            </Link>
          </div>
        </div>
      </section>

      {/* Personal Note */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="bg-accent-soft rounded-2xl p-8 border-l-4 border-primary">
            <h2 className="font-heading text-2xl font-bold text-secondary mb-4">
              My Honest Advice
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Here's what I tell everyone who calls me about a settlement offer: Don't accept anything until you fully understand your injuries and their long-term impact.
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              I've seen too many people accept quick settlements, only to realize months later that their injuries required ongoing treatment they can no longer afford. Once you sign that release, there's no going back.
            </p>
            <p className="text-slate-700 leading-relaxed">
              If you've received an offer, call or text me. I'll give you an honest assessment of whether it's fair—no charge, no obligation. If the offer is actually reasonable (it happens occasionally), I'll tell you that too.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              Common Questions About Settlement Offers
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="font-heading font-semibold text-secondary mb-3">{faq.question}</h3>
                <p className="text-slate-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Concepts - Entity SEO */}
      <section className="py-12 bg-white border-t border-slate-100">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <h2 className="font-heading text-xl font-semibold text-secondary mb-6 text-center">
            Understanding Settlements
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <a href="https://en.wikipedia.org/wiki/Settlement_(litigation)" target="_blank" rel="noopener noreferrer" className="bg-slate-50 p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Legal Settlement <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Release_(law)" target="_blank" rel="noopener noreferrer" className="bg-slate-50 p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Release of Claims <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Damages" target="_blank" rel="noopener noreferrer" className="bg-slate-50 p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Legal Damages <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Pain_and_suffering" target="_blank" rel="noopener noreferrer" className="bg-slate-50 p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Pain and Suffering <ExternalLink className="w-3 h-3" />
              </span>
            </a>
          </div>
          <p className="text-xs text-slate-400 text-center mt-4">
            Learn more about these legal concepts on Wikipedia
          </p>
        </div>
      </section>

      {/* Related Car Accident Resources */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h2 className="font-heading text-xl font-semibold text-secondary mb-6 text-center">
            Related Car Accident Resources
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/car-crashes" className="flex items-center gap-3 bg-slate-50 p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-md transition-all group">
              <TrendingUp className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium text-secondary group-hover:text-primary transition-colors">Car Accident Claims</p>
                <p className="text-sm text-slate-500">Overview of the car accident claim process</p>
              </div>
            </Link>
            <Link to="/how-much-is-my-case-worth" className="flex items-center gap-3 bg-slate-50 p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-md transition-all group">
              <DollarSign className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium text-secondary group-hover:text-primary transition-colors">How Much Is My Case Worth?</p>
                <p className="text-sm text-slate-500">Factors that determine case value</p>
              </div>
            </Link>
            <Link to="/should-i-talk-to-insurance" className="flex items-center gap-3 bg-slate-50 p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-md transition-all group">
              <Shield className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium text-secondary group-hover:text-primary transition-colors">Talking to Insurance</p>
                <p className="text-sm text-slate-500">What to say—and what not to say</p>
              </div>
            </Link>
            <Link to="/everett-car-accident-lawyer" className="flex items-center gap-3 bg-slate-50 p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-md transition-all group">
              <ArrowRight className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium text-secondary group-hover:text-primary transition-colors">Everett Car Accident Lawyer</p>
                <p className="text-sm text-slate-500">Local representation in Snohomish County</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-6">
            Got a Settlement Offer? Let Me Review It.
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            I'll tell you honestly whether it's fair—no charge, no obligation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-primary/90 transition-all"
            >
              <Phone className="w-5 h-5" />
              Call (425) 675-1224
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              Text Me
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShouldIAcceptSettlementPage;
